import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const lines = ["Become a", "Fearless", "Investrader"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.4 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.3]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_40%,rgba(56,189,248,0.14),transparent_55%)]" />
      </motion.div>

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-burgundy" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-burgundy">
            The Investrading Coaching Accelerator
          </span>
        </motion.div>

        <h1 className="font-display font-medium text-white tracking-tighter leading-[0.9] text-6xl md:text-8xl lg:text-[9.5rem]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "italic text-burgundy text-glow" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="max-w-md text-slate-300 text-base md:text-lg leading-relaxed"
          >
            A private mentorship blending fundamental analysis, technical timing
            and options strategy — coached by Diego Taira, 25+ years in the
            markets.
          </motion.p>

          <div className="flex flex-col items-start lg:items-end gap-4">
            <span className="signature-anim font-display italic text-3xl text-white/90">
              Diego Taira
            </span>
            <div className="flex flex-wrap gap-4">
              <a
                href="#pricing"
                data-testid="hero-cta-enroll"
                className="group bg-burgundy hover:bg-burgundy-hover text-ink font-semibold font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full transition-colors duration-300"
              >
                Enroll in the cohort
              </a>
              <a
                href="#about"
                data-testid="hero-cta-about"
                className="border border-white/20 hover:border-white/50 text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full transition-colors duration-300"
              >
                Meet Diego
              </a>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-slate-400 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
